import { PlansRow, SubscriptionPlan } from "@/types/index";

// ============================================
// HOLIDAY SALE CONFIGURATION
// ============================================

// Holiday Sale expires Friday, December 26, 2025 at 11:59:59 PM EST
export const BLACK_FRIDAY_END = new Date('2025-12-27T04:59:59Z');

// Holiday discount percentages
// Monthly: discount applies to first 6 months only
// Yearly: discount applies to the full year
export const BF_DISCOUNTS = {
  advanced: { monthly: 0.25, yearly: 0.50 }, // 25% off monthly (6mo), 50% off yearly
  pro: { monthly: 0.35, yearly: 0.60 }, // 35% off monthly (6mo), 60% off yearly
} as const;

// Holiday Sale Stripe Promotion Code IDs
// TODO: Replace with your actual Stripe promotion code IDs (promo_xxx)
export const BF_PROMO_CODES = {
  advancedMonthly: 'promo_1SZSBBIY2GrKnEaFybJYAaMO', // 25% off, 6 months
  advancedYearly: 'promo_1SZS9VIY2GrKnEaFPnxSLn7E',   // 50% off
  proMonthly: 'promo_1SZS8IIY2GrKnEaF2ZZBArjs',           // 35% off, 6 months
  proYearly: 'promo_1SZS8mIY2GrKnEaFS0MSIDO1',             // 60% off
} as const;

// Helper to check if Holiday Sale is active.
// Hard-disabled per ads compliance: removes the live countdown / urgency UX
// across all banners and prevents the checkout flow from auto-applying holiday
// promo codes. Re-enable explicitly when running a real, dated promo.
export function isHolidaySaleActive(): boolean {
  return false;
}

// ============================================
// QUANT TIER LAUNCH
// ============================================

// When the Quant tier shipped. Pro/Advanced subscriptions started BEFORE this
// moment are grandfathered into Options Flows (they subscribed when Flows was
// a Pro feature). A cancel + re-subscribe stamps a fresh
// stripeSubscriptionStarted via the checkout webhook, which is after this
// date, so re-joining Pro users lose Flows by design.
// NOTE: set this to the actual production deploy timestamp of the Quant launch.
export const QUANT_LAUNCH_DATE = new Date("2026-08-07T00:00:00Z");

// Intro offer for the Quant plan: countdown + limited seats on the landing
// page. Ends Wednesday, August 12, 2026 at midnight Pacific (end of day).
export const QUANT_INTRO_OFFER = {
  endsAt: new Date("2026-08-13T07:00:00Z"),
  seatLimit: 50,
  // Full (post-intro) prices, shown struck through next to the intro prices.
  // Subscribers who join during the intro are grandfathered at the intro price
  // for as long as they stay subscribed.
  fullMonthlyPrice: 129,
  fullYearlyPrice: 1249,
} as const;

export function isQuantIntroActive(now: Date = new Date()): boolean {
  return now.getTime() < QUANT_INTRO_OFFER.endsAt.getTime();
}

// ============================================
// GEX PLAN BENEFITS (shared between pricing cards)
// ============================================

export const GEX_BENEFITS = [
  "GEX Heatmap",
  "GEX AI (1-click GEX, Vanna & Charm analysis)",
  "GEX Screener (alpha)",
  "Historical GEX & Heatmap Replay",
  "Live GEX PlayTape",
  "Gamma Flip Alerts",
  "Put Wall / Call Wall Alerts",
  "Multi-ticker Custom Workspace (auto refresh)",
  "Vanna & Charm Dashboard",
  "Max Pain",
  "MCP access (GEX features)",
] as const;

export const PRO_EXTRA_BENEFITS = [
  "Full Wheel Lifecycle (find CSPs, covered calls)",
  "Avoid assignment with Roll Finder",
  //"Suggested actions \u2013 generate income & manage risk",
  "Broker-integrated Portfolio Insights & Suggested Actions",
  "Complete Options Journal",
  "QW Intelligence \u2013 AI trade ideas",
  "Real-time Alerts",
  "Copy Trading Access",
  "Unlimited GEX Features",
  "Desktop App (TradingView overlay)",
  "MCP access (Pro features)",
] as const;

export const QUANT_EXTRA_BENEFITS = [
  "Options Flows \u2013 live SPY/SPX premium flow",
  "Full API access",
  "Full MCP access (including Flows)",
  "Access to alpha features",
  "Premium support",
] as const;

// ============================================
// PRICING DATA
// ============================================

export const pricingData: SubscriptionPlan[] = [
  {
    title: "Free",
    description: "Free Forever",
    benefits: [
      "Play with all wheel strategy tools using demo data",
      "Delayed GEX data",
      "No signup or card required",
      "Limited to 5 results per feature",
    ],
    limitations: [],
    prices: {
      monthly: 0,
      yearly: 0,
    },
    stripeIds: {
      monthly: null,
      yearly: null,
    },
  },
  {
    title: "QuantWheel GEX",
    description: "$1 for 7 days, then:",
    benefits: [...GEX_BENEFITS],
    limitations: [],
    prices: {
      monthly: 27,
      yearly: 228,
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_GEX_MONTHLY_PLAN_ID || null,
      yearly: process.env.NEXT_PUBLIC_STRIPE_GEX_YEARLY_PLAN_ID || null,
    },
  },
  {
    title: "QuantWheel PRO",
    description: "$1 for 7 days, then:",
    benefits: [
      ...GEX_BENEFITS,
      ...PRO_EXTRA_BENEFITS,
    ],
    limitations: [],
    prices: {
      monthly: 47,
      yearly: 451,
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PLAN_ID || null,
      yearly: process.env.NEXT_PUBLIC_STRIPE_PRO_YEARLY_PLAN_ID || null,
    },
  },
  {
    title: "QuantWheel Quant",
    description: "$1 for 7 days, then:",
    benefits: [
      ...GEX_BENEFITS,
      ...PRO_EXTRA_BENEFITS,
      ...QUANT_EXTRA_BENEFITS,
    ],
    limitations: [],
    prices: {
      monthly: 79,
      yearly: 479,
      // Billed every 3 months (~$60/mo) - only the Quant plan has this.
      quarterly: 179,
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_QUANT_MONTHLY_PLAN_ID || null,
      quarterly: process.env.NEXT_PUBLIC_STRIPE_QUANT_QUARTERLY_PLAN_ID || null,
      yearly: process.env.NEXT_PUBLIC_STRIPE_QUANT_YEARLY_PLAN_ID || null,
    },
  },
];

// Legacy Advanced plan - kept for existing subscribers
export const legacyAdvancedPlan: SubscriptionPlan = {
  title: "Advanced",
  description: "Legacy plan",
  benefits: [
    "Unlimited Options screener",
    "20 QW Intelligence prompts/day",
    "Copy trading access",
    "Broker-integrated options journal",
    "System Watchlists",
    "5 Watchlists (up to 100 tickers)",
    "3 Alert definitions",
    "500 Alerts / month (5-min delay)",
    "Unlimited optimal rolls",
    "20 AI risk analyses/day",
    "GEX: 10 widgets, 10 flow alerts/day",
    "Options strategy explorer",
    "Ability to influence new features",
  ],
  limitations: [],
  prices: {
    monthly: 47,
    yearly: 451,
  },
  stripeIds: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_ADVANCED_MONTHLY_PLAN_ID || null,
    yearly: process.env.NEXT_PUBLIC_STRIPE_ADVANCED_YEARLY_PLAN_ID || null,
  },
};

// Legacy old Pro plan ($97/mo) - kept for subscribers still on the old price IDs
export const legacyOldProPlan: SubscriptionPlan = {
  title: "QuantWheel PRO",
  description: "Legacy Pro plan",
  benefits: [...GEX_BENEFITS, ...PRO_EXTRA_BENEFITS],
  limitations: [],
  prices: {
    monthly: 97,
    yearly: 931,
  },
  stripeIds: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_OLD_PLAN_ID || null,
    yearly: process.env.NEXT_PUBLIC_STRIPE_PRO_YEARLY_OLD_PLAN_ID || null,
  },
};

// All plans including legacy (used for plan lookup by price ID)
export const allPlans: SubscriptionPlan[] = [...pricingData, legacyAdvancedPlan, legacyOldProPlan];

export const plansColumns = [
  "QuantWheel",
  "Other Screeners",
  "Other Copy Traders",
  "Manual Journals",
] as const;

export const comparePlans: PlansRow[] = [
  {
    feature: "Options Screener",
    QuantWheel: "Real-time, simple filters for income strategies",
    "Other Screeners": "Slow, clunky, complicated",
    "Other Copy Traders": null,
    "Manual Journals": null,
    tooltip: "Our screener focuses on income-generating strategies like CSPs and covered calls. Filter by premium, return %, delta, DTE, and more.",
  },
  {
    feature: "AI Assistant",
    QuantWheel: "QW Intelligence - ask in plain English",
    "Other Screeners": null,
    "Other Copy Traders": null,
    "Manual Journals": null,
    tooltip: "Ask questions like 'Find me high-yield CSPs on tech stocks' and get instant results. No complex filters to configure.",
  },
  {
    feature: "Copy Trading",
    QuantWheel: "Options trades from experienced traders",
    "Other Screeners": null,
    "Other Copy Traders": "Stock only",
    "Manual Journals": null,
    tooltip: "Follow and learn from experienced options traders. See their trades in real-time (with broker-specific delays).",
  },
  {
    feature: "Options Journal",
    QuantWheel: "Auto-synced from 15+ brokers",
    "Other Screeners": null,
    "Other Copy Traders": null,
    "Manual Journals": "Hours of manual entry",
    tooltip: "Connect your broker and automatically track all your options trades. See P&L, win rate, and performance analytics.",
  },
  {
    feature: "Real-time Alerts",
    QuantWheel: "1-click setup, unlimited tickers",
    "Other Screeners": "Limited or delayed",
    "Other Copy Traders": null,
    "Manual Journals": null,
    tooltip: "Get notified via email or Discord when perfect trade setups appear. Set alerts in seconds, not hours.",
  },
  {
    feature: "Broker Integrations",
    QuantWheel: "15+ brokers supported",
    "Other Screeners": null,
    "Other Copy Traders": "Limited",
    "Manual Journals": null,
    tooltip: "Connect Schwab, Fidelity, Robinhood, IBKR, Tastytrade, Webull, E*Trade, and many more.",
  },
  {
    feature: "P&L Analytics",
    QuantWheel: "Automatic charts & insights",
    "Other Screeners": null,
    "Other Copy Traders": null,
    "Manual Journals": "Calculate yourself",
    tooltip: "See your win rate, average return, performance by strategy, and more - all calculated automatically.",
  },
  {
    feature: "Time Investment",
    QuantWheel: "Minutes per week",
    "Other Screeners": "Varies",
    "Other Copy Traders": "Varies",
    "Manual Journals": "Hours per week",
    tooltip: "Spend your time trading, not tracking. QuantWheel automates the boring stuff.",
  },
  {
    feature: "Starting Price",
    QuantWheel: "Free sandbox, or GEX from $27/mo, PRO from $47/mo",
    "Other Screeners": "$49-99/mo",
    "Other Copy Traders": "$20-50/mo",
    "Manual Journals": "Free (but costs time)",
    tooltip: "Try our free sandbox, or start with GEX at $27/mo or PRO at $47/mo. Cancel anytime.",
  },
];
